import React from 'react';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import { INTERIOR_ROOM_TYPES } from '../../data/interiorRoomTypes';
import { INTERIOR_THEMES } from '../../data/interiorThemes';
import { INTERIOR_PALETTES } from '../../data/interiorPalettes';
import { Bed, DoorOpen, Square, Palette, Droplets, Home, Sofa, Clock } from 'lucide-react';

export const InteriorControls: React.FC = () => {
    const {
        roomType,
        setRoomType,
        doorCount,
        setDoorCount,
        windowCount,
        setWindowCount,
        interiorTheme,
        setInteriorTheme,
        interiorColorPaletteId,
        setInteriorColorPaletteId,
        timeOfDay,
        setTimeOfDay
    } = useArchitectureStore();

    const getCurrentRoomType = () => INTERIOR_ROOM_TYPES.find(rt => rt.id === roomType) || INTERIOR_ROOM_TYPES[0];
    const getCurrentTheme = () => INTERIOR_THEMES.find(t => t.id === interiorTheme) || INTERIOR_THEMES[0];
    const getCurrentPalette = () => INTERIOR_PALETTES.find(p => p.id === interiorColorPaletteId) || null;

    const currentRoom = getCurrentRoomType();
    const currentTheme = getCurrentTheme();
    const currentPalette = getCurrentPalette();

    return (
        <div className="space-y-6 p-4">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/20 rounded-xl">
                    <Home size={20} className="text-indigo-400" />
                </div>
                <div>
                    <h3 className="text-sm font-bold text-white tracking-tight">Interior Design</h3>
                    <p className="text-xs text-zinc-500">Configure room layout and styling</p>
                </div>
            </div>

            {/* Room Type Selector */}
            <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-zinc-400 uppercase tracking-widest">
                    <Bed size={14} />
                    <span>Room Type</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {INTERIOR_ROOM_TYPES.map(room => (
                        <button
                            key={room.id}
                            onClick={() => setRoomType(room.id)}
                            className={`p-3 rounded-xl border transition-all flex flex-col items-center justify-center gap-1 ${roomType === room.id
                                ? 'bg-indigo-500/20 border-indigo-500/50 text-white shadow-lg shadow-indigo-500/10'
                                : 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10 hover:text-zinc-300'
                                }`}
                        >
                            <span className="text-lg">{room.icon}</span>
                            <span className="text-xs font-medium">{room.name}</span>
                        </button>
                    ))}
                </div>
                {currentRoom && (
                    <p className="text-xs text-zinc-500 mt-1 italic">{currentRoom.description}</p>
                )}
            </div>

            {/* Door & Window Count */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-zinc-400 uppercase tracking-widest">
                        <DoorOpen size={14} />
                        <span>Doors</span>
                    </div>
                    <div className="flex items-center bg-black/40 p-1 rounded-xl border border-white/5">
                        {[1, 2, 3, 4].map(count => (
                            <button
                                key={count}
                                onClick={() => setDoorCount(count)}
                                className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${doorCount === count
                                    ? 'bg-indigo-600 text-white shadow-lg'
                                    : 'text-zinc-500 hover:text-zinc-300'
                                    }`}
                            >
                                {count}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-zinc-400 uppercase tracking-widest">
                        <Square size={14} />
                        <span>Windows</span>
                    </div>
                    <div className="flex items-center bg-black/40 p-1 rounded-xl border border-white/5">
                        {[1, 2, 3, 4].map(count => (
                            <button
                                key={count}
                                onClick={() => setWindowCount(count)}
                                className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${windowCount === count
                                    ? 'bg-indigo-600 text-white shadow-lg'
                                    : 'text-zinc-500 hover:text-zinc-300'
                                    }`}
                            >
                                {count}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Design Theme */}
            <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-zinc-400 uppercase tracking-widest">
                    <Sofa size={14} />
                    <span>Design Theme</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {INTERIOR_THEMES.map(theme => (
                        <button
                            key={theme.id}
                            onClick={() => setInteriorTheme(theme.id)}
                            className={`p-3 rounded-xl border transition-all flex flex-col items-center gap-1 ${interiorTheme === theme.id
                                ? 'bg-indigo-500/20 border-indigo-500/50 text-white'
                                : 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10'
                                }`}
                        >
                            <span className="text-lg">{theme.icon}</span>
                            <span className="text-xs font-medium">{theme.name}</span>
                        </button>
                    ))}
                </div>
                {currentTheme && (
                    <p className="text-xs text-zinc-500 italic">{currentTheme.moodDescription}</p>
                )}
            </div>

            {/* Color Palette */}
            <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-zinc-400 uppercase tracking-widest">
                    <Palette size={14} />
                    <span>Color Palette</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {INTERIOR_PALETTES.map(palette => (
                        <button
                            key={palette.id}
                            onClick={() => setInteriorColorPaletteId(palette.id)}
                            className={`p-3 rounded-xl border transition-all flex flex-col items-center gap-1 ${interiorColorPaletteId === palette.id
                                ? 'bg-indigo-500/20 border-indigo-500/50 text-white'
                                : 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10'
                                }`}
                        >
                            <div className="flex gap-1">
                                {palette.colors.slice(0, 3).map((color, idx) => (
                                    <div
                                        key={idx}
                                        className="w-4 h-4 rounded-full"
                                        style={{ backgroundColor: color }}
                                    />
                                ))}
                            </div>
                            <span className="text-xs font-medium mt-1">{palette.name}</span>
                        </button>
                    ))}
                </div>
                {currentPalette && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <Droplets size={12} />
                        <span>Primary: {currentPalette.colors[0]}</span>
                    </div>
                )}
            </div>

            {/* Time of Day */}
            <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-zinc-400 uppercase tracking-widest">
                    <Clock size={14} />
                    <span>Lighting Time</span>
                </div>
                <div className="flex items-center bg-black/40 p-1 rounded-xl border border-white/5">
                    {['Day', 'Sunset', 'Night'].map(time => (
                        <button
                            key={time}
                            onClick={() => setTimeOfDay(time)}
                            className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${timeOfDay === time
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'text-zinc-500 hover:text-zinc-300'
                                }`}
                        >
                            {time}
                        </button>
                    ))}
                </div>
            </div>

            {/* Current Configuration Summary */}
            <div className="pt-4 border-t border-white/10">
                <div className="text-xs text-zinc-500 space-y-1">
                    <div className="flex justify-between">
                        <span>Current Room:</span>
                        <span className="text-white">{currentRoom?.name}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Doors/Windows:</span>
                        <span className="text-white">{doorCount} / {windowCount}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Theme:</span>
                        <span className="text-white">{currentTheme?.name}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Palette:</span>
                        <span className="text-white">{currentPalette?.name || 'Default'}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Lighting:</span>
                        <span className="text-white">{timeOfDay}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InteriorControls;